/**
 * Parent → children fan-out table.
 *
 * Shared by the Bulk Jobs detail page (one child per chassis) and the
 * chassis Manual ops → Add service form (one child per batch of ONUs
 * under an ``add_service_from_scope`` arm job). Both read
 * ``GET /bulk-jobs/{parentId}`` through ``normaliseBulkJobDetail`` — the
 * API returns a FLAT parent (``BulkJobDetail`` in
 * apps/web/app/schemas/bulk_jobs.py), not ``{parent, children}``; reading
 * ``data.parent`` directly crashed the Add service progress view.
 */
import { Link } from "react-router-dom";

import { Badge, type BadgeTone } from "./Badge";

export type ChildJobRow = {
  id: string;
  chassis_id: number | null;
  chassis_name: string | null;
  status: string;
  completed_targets: number | null;
  total_targets: number | null;
  error_summary: string | null;
};

/** Wire shape of ``GET /bulk-jobs/{id}``. */
export type BulkJobDetailWire = {
  parent_job_id: string;
  kind: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  total_targets: number | null;
  completed_targets: number | null;
  succeeded: number;
  failed: number;
  running: number;
  children: {
    job_id: string;
    chassis_id: number | null;
    chassis_name: string | null;
    rendered_name: string;
    status: string;
    started_at: string | null;
    completed_at: string | null;
    total_targets?: number | null;
    completed_targets?: number | null;
    error_summary?: string | null;
  }[];
};

export type BulkJobParent = {
  id: string;
  kind: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  total_targets: number | null;
  completed_targets: number | null;
  succeeded: number;
  failed: number;
  running: number;
};

export type BulkJobDetailView = {
  parent: BulkJobParent;
  children: ChildJobRow[];
};

export function normaliseBulkJobDetail(w: BulkJobDetailWire): BulkJobDetailView {
  return {
    parent: {
      id: w.parent_job_id,
      kind: w.kind,
      status: w.status,
      started_at: w.started_at,
      completed_at: w.completed_at,
      total_targets: w.total_targets,
      completed_targets: w.completed_targets,
      succeeded: w.succeeded,
      failed: w.failed,
      running: w.running,
    },
    children: (w.children ?? []).map((c) => ({
      id: c.job_id,
      chassis_id: c.chassis_id,
      chassis_name: c.chassis_name,
      status: c.status,
      completed_targets: c.completed_targets ?? null,
      total_targets: c.total_targets ?? null,
      error_summary: c.error_summary ?? null,
    })),
  };
}

export const CHILD_JOB_STATUS_TONE: Record<string, BadgeTone> = {
  queued: "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  partial: "warning",
  cancelled: "muted",
};

export function ChildJobsTable({
  rows,
  firstColumn = "chassis",
}: {
  rows: ChildJobRow[];
  /** ``chassis`` = link to the child's chassis (bulk fan-out);
   *  ``batch`` = 1-based batch number (same-chassis batched runs). */
  firstColumn?: "chassis" | "batch";
}) {
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-2 py-1">
            {firstColumn === "chassis" ? "Chassis" : "Batch"}
          </th>
          <th className="px-2 py-1">Status</th>
          <th className="px-2 py-1">Progress</th>
          <th className="px-2 py-1">Error summary</th>
          <th className="px-2 py-1 text-right">Job</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800/40 text-slate-300">
        {rows.map((c, i) => (
          <tr key={c.id}>
            <td className="px-2 py-1.5">
              {firstColumn === "chassis" ? (
                <Link
                  to={`/chassis/${c.chassis_id}`}
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  {c.chassis_name ?? `chassis-${c.chassis_id}`}
                </Link>
              ) : (
                <span className="font-mono text-slate-400">#{i + 1}</span>
              )}
            </td>
            <td className="px-2 py-1.5">
              <Badge tone={CHILD_JOB_STATUS_TONE[c.status] ?? "neutral"}>
                {c.status}
              </Badge>
            </td>
            <td className="px-2 py-1.5 font-mono">
              {c.completed_targets ?? 0}/{c.total_targets ?? "?"}
            </td>
            <td className="px-2 py-1.5 text-[11px] text-slate-400">
              {c.error_summary ?? "—"}
            </td>
            <td className="px-2 py-1.5 text-right">
              <Link
                to={`/settings/jobs/${c.id}`}
                className="text-emerald-400 hover:text-emerald-300"
              >
                open
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
